import * as React from 'react';
import { useRef, useEffect } from 'react';
import { UitkText } from 'uitk-react-text';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-seat-map.css';
import { SeatVariant } from './types.esm.js';
import { UitkSeatMapButton } from './uitk-seat-map-button.esm.js';
import { normalMapSizeThreshold } from './utils.esm.js';

var getClassNameList = classNames.bind(styles);
var aisleMarkerCell = function (seat, seatKey, rowNum) { return (React.createElement(UitkSpacing, { padding: "one", key: seatKey },
    React.createElement("th", { scope: "row", className: getClassNameList('uitk-seat-map-headers') }, rowNum && (React.createElement(React.Fragment, null,
        React.createElement(UitkText, { size: 200, align: "center" },
            React.createElement("span", { id: seatKey, "aria-hidden": "true" }, rowNum)),
        React.createElement("label", { htmlFor: seatKey },
            React.createElement("span", { className: "is-visually-hidden" }, seat.accessibilityMessage))))))); };
var rowMarkerCell = function (seat, seatKey) { return (React.createElement(UitkSpacing, { padding: { inlinestart: 'four', inlineend: 'four', blockstart: 'six', blockend: 'four' }, key: seatKey },
    React.createElement("td", null, seat.name && !seat.isEmpty && (React.createElement(UitkText, { size: 200, align: "center", inline: true }, seat.name))))); };
var columnMarkerCell = function (seatKey, columnNum) { return (React.createElement(UitkSpacing, { padding: { blockstart: 'two', blockend: 'one' }, key: seatKey },
    React.createElement("th", { scope: "col", className: getClassNameList('uitk-seat-map-headers') },
        React.createElement(UitkText, { size: 200, inline: true }, columnNum)))); };
/* eslint-disable max-params */
var seatCell = function (seat, onClickButton, seatKey, seatConfirmedDetails, seatSelected) {
    var _a, _b, _c, _d, _e;
    return (React.createElement(UitkSpacing, { padding: seatConfirmedDetails && seatSelected === seat.name ? undefined : 'one', key: seatKey },
        React.createElement("td", null,
            React.createElement(UitkSeatMapButton, { isConfirmed: !!seatConfirmedDetails, disabled: !((_a = seat.action) === null || _a === void 0 ? void 0 : _a.enable), id: seat.name || undefined, checked: seatSelected === seat.name, name: "seatMap", icon: seatConfirmedDetails ? undefined : (_c = (_b = seat.action) === null || _b === void 0 ? void 0 : _b.icon) === null || _c === void 0 ? void 0 : _c.id, onChange: onClickButton, altText: seat.accessibilityMessage || undefined, btnText: (_d = seatConfirmedDetails === null || seatConfirmedDetails === void 0 ? void 0 : seatConfirmedDetails.travelerIndex) !== null && _d !== void 0 ? _d : (_e = seat.action) === null || _e === void 0 ? void 0 : _e.displayAction }))));
};
/* eslint-enable max-params */
var galleyCell = function (seat, seatKey) {
    var _a;
    return (React.createElement(UitkSpacing, { padding: "one", key: seatKey },
        React.createElement("td", { colSpan: seat.span || undefined }, seat.icon && React.createElement(UitkIcon, { name: (_a = seat.icon) === null || _a === void 0 ? void 0 : _a.id, theme: "default", size: UitkIconSize.SMALL }))));
};
var exitMarkerCell = function (seatKey, icon) { return (React.createElement(UitkSpacing, { padding: "one", key: seatKey },
    React.createElement("td", null, icon && (React.createElement(UitkIcon, { name: icon.id, id: "".concat(icon.id, "+").concat(seatKey), title: icon.description, size: UitkIconSize.SMALL }))))); };
var UitkSeatMapCabin = function (props) {
    var onSeatButtonClickCB = props.onSeatButtonClickCB, seatRows = props.seatRows, seatConfirmedDetails = props.seatConfirmedDetails, seatSelected = props.seatSelected, maxColumns = props.maxColumns, rowIndexRef = props.rowIndexRef;
    var cabinRowRef = useRef(null);
    var scrollTo = function (ref) {
        if (ref) {
            try {
                ref.scrollIntoView({
                    behavior: 'auto',
                    block: 'nearest',
                    inline: 'center'
                });
            }
            catch (_a) {
                return false;
            }
        }
    };
    useEffect(function () {
        if (maxColumns < normalMapSizeThreshold) {
            scrollTo(cabinRowRef.current);
        }
    }, [maxColumns]);
    var onClickButton = React.useCallback(function (e) {
        onSeatButtonClickCB(e.target.id);
    }, [onSeatButtonClickCB]);
    var getSeatCell = function (seat, index, seatIndex) {
        var seatKey = "seat-cell-".concat(index, "+").concat(seatIndex);
        var seatConfirmedDetail = seatConfirmedDetails === null || seatConfirmedDetails === void 0 ? void 0 : seatConfirmedDetails.find(function (scd) { return scd.name === seat.name; });
        switch (seat.variant) {
            case SeatVariant.COLUMN:
                return columnMarkerCell(seatKey, seat.name);
            case SeatVariant.AISLE:
                return aisleMarkerCell(seat, seatKey, seat.name);
            case SeatVariant.EXIT:
                return exitMarkerCell(seatKey, seat.icon);
            case SeatVariant.GALLEY || SeatVariant.RESTROOM:
                return galleyCell(seat, seatKey);
            case SeatVariant.ROW:
                return rowMarkerCell(seat, seatKey);
            default:
                return seatCell(seat, onClickButton, seatKey, seatConfirmedDetail, seatSelected);
        }
    };
    if (!seatRows) {
        return null;
    }
    return (React.createElement("div", null,
        React.createElement(UitkSpacing, { margin: { blockend: 'twelve' } },
            React.createElement("table", { className: getClassNameList('uitk-seat-map-grid') },
                React.createElement("tbody", null, seatRows.map(function (seatRow, index) {
                    var _a;
                    return (React.createElement("tr", { key: index, ref: index === rowIndexRef ? cabinRowRef : undefined }, (_a = seatRow.seatMapRowSeats) === null || _a === void 0 ? void 0 : _a.map(function (seat, seatIndex) { return getSeatCell(seat, index, seatIndex); })));
                }))))));
};

export { UitkSeatMapCabin };
